import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import './App.css';

const App = () => {
  const [sessionId, setSessionId] = useState(() => uuidv4());
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: 'Xin chào! Tôi là trợ lý du lịch Tây Ninh. Tôi có thể giúp bạn tìm kiếm thông tin về các địa điểm, khách sạn, nhà hàng và hoạt động du lịch tại Tây Ninh. Bạn cần tìm hiểu về điều gì?'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleReset = () => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    setMessages([
      {
        role: 'bot',
        content: 'Xin chào! Tôi là trợ lý du lịch Tây Ninh. Tôi có thể giúp bạn tìm kiếm thông tin về các địa điểm, khách sạn, nhà hàng và hoạt động du lịch tại Tây Ninh. Bạn cần tìm hiểu về điều gì?'
      }
    ]);
    setInputMessage('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) {
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Thêm tin nhắn của user
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Tạo FormData
      const formData = new FormData();
      formData.append('message', userMessage);
      formData.append('sessionId', sessionId);

      // Gọi API
      const response = await axios.post(
        'https://llm.dxtayninh.cloud/webhook/api/chatbot/dulich',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Xử lý response
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const botResponse = response.data[0].output;
        
        // Thêm tin nhắn rỗng của bot
        setMessages(prev => [...prev, { role: 'bot', content: '' }]);
        
        // Streaming effect - hiển thị từng ký tự
        let currentIndex = 0;
        const streamInterval = setInterval(() => {
          if (currentIndex < botResponse.length) {
            const chunk = botResponse.slice(0, currentIndex + 1);
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: 'bot',
                content: chunk
              };
              return newMessages;
            });
            currentIndex += 2; // Tăng tốc độ streaming
          } else {
            clearInterval(streamInterval);
            setIsLoading(false);
          }
        }, 20);
      } else {
        setMessages(prev => [
          ...prev,
          { role: 'bot', content: 'Xin lỗi, tôi không thể xử lý yêu cầu của bạn lúc này.' }
        ]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [
        ...prev,
        { role: 'bot', content: 'Xin lỗi, đã có lỗi xảy ra. Vui lòng thử lại sau.' }
      ]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <div className="app-container">
      <div className="chat-container">
        {/* Header */}
        <div className="chat-header">
          <div className="header-left">
            <div className="header-icon">
              <svg 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
                />
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
                />
              </svg>
            </div>
            <div className="header-info">
              <h1>Chatbot Du Lịch Tây Ninh</h1>
              <p>Trợ lý thông minh</p>
            </div>
          </div>
          <button
            onClick={handleReset}
            className="reset-button"
            aria-label="Reset cuộc hội thoại"
            tabIndex={0}
          >
            <svg 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
            <span>Reset</span>
          </button>
        </div>

        {/* Messages Area */}
        <div className="messages-container">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`message-wrapper ${message.role}`}
            >
              <div className={`message-bubble ${message.role}`}>
                <div className="message-content">
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p>{children}</p>,
                      ul: ({ children }) => <ul>{children}</ul>,
                      ol: ({ children }) => <ol>{children}</ol>,
                      li: ({ children }) => <li>{children}</li>,
                      strong: ({ children }) => <strong>{children}</strong>,
                      em: ({ children }) => <em>{children}</em>,
                      code: ({ children }) => <code>{children}</code>,
                      a: ({ href, children }) => (
                        <a 
                          href={href} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="loading-wrapper">
              <div className="loading-bubble">
                <div className="loading-dots">
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="input-container">
          <form onSubmit={handleSendMessage} className="input-form">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nhập câu hỏi của bạn..."
              className="input-field"
              disabled={isLoading}
              aria-label="Nhập tin nhắn"
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              className="send-button"
              aria-label="Gửi tin nhắn"
            >
              <span>Gửi</span>
              <svg 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" 
                />
              </svg>
            </button>
          </form>
          <p className="session-id">
            Session ID: {sessionId.slice(0, 8)}...
          </p>
        </div>
      </div>
    </div>
  );
};

export default App;
